---
title: '@core/tracking'
metaTitle: 'Admin UI | Core - Tracking package'
metaDescription: 'How to use Amplitude in your package'
---

Amplitude aims to enhance our understanding of user behavior within the platform by tracking their interactions and engagement. By analyzing this data, we can make informed decisions to improve user experience, identify popular features, and prioritize future developments.

## Getting Started

**Installation**

```bash
cd ./your-package

pnpm add "@core/tracking@*"

or

pnpm add "@core/tracking@*" --recursive --filter <your-package>
```

## User Guide

### track

The track function is the core method used to send events to Amplitude.

It is defined as:

```javascript
track = (event: TrackEvent | string, data: TrackData = {})
```
- **`event`**:
 This can be either a `TrackEvent` object or a `string` (old solution which is now deprecated ).
- **`data`**: 
An object of key-value pairs sent as event properties. 

```javascript
type TrackData = Record<string, any>;
```

If our event is of `TrackEvent`type, it conforms to the following signature:

```javascript
type TrackEvent = {
action: ActionType;
subject: string;
};
```
This type has been created to help implementers follow event taxonomy standards as much as possible.
`action` is an `ActionType`, which is a predefined list of allowed actions (viewed, clicked, etc.), and subject is the string that defines the event (ex.: source add).
To learn more about Amplitude event taxonomy and its intricacies, please read the [Amplitude taxonomy documentation](https://coveord.atlassian.net/wiki/spaces/PRODUCT/pages/2898919518/Amplitude+Taxonomy).
> Note: The **track** method must only be used in a `tracking` folder at the root of your package's `src` folder.
> This rule is mainly related to code review.
> Lint rules and Sentry alerts have also been setup to help avoid problematic usage of `TrackEvent` within `track`.


Every call to `track` **automatically enriches the event** with the following contextual properties based on the page the user is currently on:

- **`section`**: Group of elements the page belongs to (e.g., `'Content'`, `'Search'`, etc.), determined based on the current route.
- **`subSection`**: Specific page within the section (e.g., `'Sources'`, `'logs-browser'`, etc.), determined based on the current route.
- **`location`**: The full pathname of the current URL (e.g., `'/acme/commerce/product-listing'`).

> These values are extracted from the current window.location. You do not need to manually specify, they are automatically added.


Given that `track` takes a `data` as a second optional parameter, this is where you can pass the rest of event properties.

**Event properties**
are additional information you'd like to tie to your event that is not included in your event name.
It allows for event generalization, i.e. when we want to trigger the same event in multiple locations but there's context we'd like to add. Here's an example:

```javascript
track({action: 'completed', subject: 'source add'}, {sourceType: 'WEB2', sourceId: 'abcd123'});
```
There's no validation for event properties, you can send whichever information you'd like as long as it's under the `Record<string, any>` format.


Example:

```
track(
  {action: 'viewed', subject: 'sources'},
  {reportId: 'xyz123'}
);

// The final event properties sent to Amplitude includes:
// {
//   reportId: 'xyz123',
//   section: 'content',
//   subsection: 'sources',
//   location: '/content/sources'
// }

```

### PageViewTracker

This component listens for route and query param changes, and determines whether a new page view should be tracked.

- Uses `useLocation` to detect changes.
- Matches the current path against `pages-mapping`.
- Supports both string and object-based rules.
- Prevents duplicate events using a unique tracking key.
- Calls `trackPageView` when a valid change is detected.

### trackPageView

Receives the matched rule and URL data to send an event to Amplitude.

- Normalizes the path (removes `/:orgId`).
- Extracts query parameters defined in the rule.
- Calls `track()` with the parsed event and extracted metadata (that are now event properties).


### How do we configure page views ? 

Page view tracking is configured using **dedicated mapping files** located in the `tracking` folder of each hub within the configuration package.
These files are organized by main application sections (for example: `content.ts`, `home.ts`, etc.)
Each file contains a **`route: eventName` mapping**, referred to as a **page mapping**, which defines what tracking event to send when a specific route is viewed.

To add a page view event:

- Add a new entry in the appropriate section file.
- Or create a new file if the section (subroute) doesn’t exist yet.


**Page mapping**: 
A **page mapping** is an object that links a route to a tracking event. Each entry in the object is called a **rule**.
Typically, two types of files work together to enable this:
1. **Page-to-event mappings**  – Define which paths should trigger which amplitude events:
    
    ```
    // tracking/pages/platform/content.ts
    import {PlatformPaths} from '../routes';
    export const content: TrackingPageViewConfigurationRules = {
      [PlatformPaths.Extensions]: 'viewed extensions',
      [PlatformPaths.ExtensionsEdit]: 'viewed extension edit',
      [PlatformPaths.ExtensionsCreate]: 'viewed extension add',
      '/:orgId/content/extensions/activity': 'viewed extensions activity',
      // ...
    };
    
    ```

2. **Path definitions** where route declarations are made to ensure consistency across the codebase:
    
    ```
    // routes/platformPaths.ts
    export const PlatformPaths = generatePaths({
      Extensions: '/content/extensions',
      ExtensionsEdit: '/content/extensions/:extensionId/edit',
      ExtensionsCreate: '/content/extensions/create',
      // ...
    });
    
    ```

**Dynamic Routes** 
If your route contains dynamic segments (e.g., `organizationId` or `extensionId`) and you still want to track it as a generalized event, define the route pattern accordingly: For example : 

```javascript
'/:organizationId/content/extensions/:extensionId': 'viewed extension edit'
```


**Rule**: is each entry of the page mapping object that links a specific route to a tracking event. It can be:

- **String-based**:
    
    ```
    '/:orgId/content/extension': 'viewed extension'
    ```
    
- **Object-based** (pages with query parameters):
    
    ```
    '/:orgId/listings': {
      default: {
        subject: 'viewed product listings manager',
        queryParams: ['trackingId'],
      },
      'tab=entries': {
        subject: 'viewed product listings manager Pages',
        queryParams: ['trackingId', 'tab'],
      },
    }
    
    ```
    
**QueryParams**: Specifies which query parameters should be extracted and sent to Amplitude as event properties. This avoids polluting events with irrelevant URL data.


### trackClickOnCoveoDocumentation

Contains a simplified function for tracking documentation clicks.

> Note: Only used in our applications.

---

